import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { Plus, X, ArrowLeft, Edit, Trash2, Printer, MessageSquare, Hash, UserPlus, ChevronRight, ChevronLeft, ArrowUpRight, Link2, Unlink, CreditCard, Ban, Lock } from 'lucide-react';
import { toLocalDateKey, todayKey, parseLocalDate, addDays } from '../lib/dateHelpers';
import PackageManager from '../components/PackageManager';
import { findPackage, packageLabel } from '../lib/packages';
import { useNotify } from '../components/NotifyProvider';
import { fetchVenueBlocksByDates, insertVenueBlock, deleteVenueBlock } from '../lib/supabaseService';

const TABLE_VENUES = [
  { id: 'reynards', name: 'Reynards Restaurant', capacity: 50 },
  { id: 'randr', name: 'R&R', capacity: 45 },
  { id: 'brewery', name: 'Brewery Bar', capacity: 60 },
];

const SITTINGS = [
  { id: 'breakfast', label: 'Breakfast', time: '9:00 – 12:00', startH: 9, endH: 12, venues: ['randr'] },
  { id: 'lunch', label: 'Lunch', time: '12:00 – 16:00', startH: 12, endH: 16, venues: ['reynards', 'randr', 'brewery'] },
  { id: 'evening', label: 'Evening', time: '16:00 – 21:00', startH: 16, endH: 21, venues: ['reynards', 'randr', 'brewery'] },
];

/**
 * Day-of-week and venue rules (updated Sep 2026 per Ronan):
 *  - R&R: open every day for breakfast & lunch; evening only at the
 *    weekend (Fri/Sat/Sun) - closes at 4pm Mon-Thu
 *  - Brewery: evening open every day; lunch only at the weekend
 *    (Fri/Sat/Sun) - opens at 4pm Mon-Thu
 *  - Reynards: only bookable for parties of 6+ (every day)
 */
const getDayOfWeek = (dateStr) => {
  if (!dateStr) return null;
  const d = parseLocalDate(dateStr);
  return d ? d.getDay() : null; // 0=Sun, 1=Mon, ..., 6=Sat
};

const isWeekend = (dateStr) => {
  const day = getDayOfWeek(dateStr);
  return day === 0 || day === 5 || day === 6;
};

// ─── SPECIAL EVENT NIGHTS ───
// One-off overrides for a specific venue+date+sitting that don't fit the
// regular day-of-week schedule — mirrors the same list in
// lib/restaurantConstants.js and public/booking-widget.html. Keep all
// three in sync (this file doesn't import restaurantConstants.js — see
// the "single source of truth" comment above on TABLE_VENUES/SITTINGS).
const SPECIAL_SITTING_OVERRIDES = [
  // NYE 2026: R&R's evening runs two fixed sittings (6pm & 8pm) at 55
  // covers each instead of the normal continuous 16:00-21:00 range at 45.
  { venueId: 'randr', date: '2026-12-31', sittingId: 'evening', capacity: 55 },
];

const getVenueCapacityForDate = (venueId, dateStr, sittingId) => {
  const override = SPECIAL_SITTING_OVERRIDES.find(
    (o) => o.venueId === venueId && o.date === dateStr && o.sittingId === sittingId
  );
  if (override) return override.capacity;
  const venue = TABLE_VENUES.find((v) => v.id === venueId);
  return venue ? venue.capacity : 0;
};

/**
 * Get sittings available for a venue on a specific date.
 * Optional `blocks` array applies override logic:
 *   'open'  block → always available (overrides schedule rules)
 *   'closed' block → always unavailable (overrides schedule rules)
 * Priority: per-sitting block > all_day block > schedule rules
 */
const getSittingsForVenueAndDate = (venueId, dateStr, blocks = []) => {
  const allDayBlock = blocks.find((b) => b.sittingId === 'all_day');
  return SITTINGS.filter((sitting) => {
    if (!sitting.venues.includes(venueId)) return false;
    const sittingBlock = blocks.find((b) => b.sittingId === sitting.id);
    if (sittingBlock?.blockType === 'open')   return true;
    if (sittingBlock?.blockType === 'closed') return false;
    if (allDayBlock?.blockType === 'open')   return true;
    if (allDayBlock?.blockType === 'closed') return false;
    // Brewery: lunch only at the weekend (Fri/Sat/Sun); evening open every day
    if (venueId === 'brewery' && sitting.id === 'lunch' && !isWeekend(dateStr)) return false;
    // R&R: evening only at the weekend (Fri/Sat/Sun) - closes at 4pm Mon-Thu
    if (venueId === 'randr' && sitting.id === 'evening' && !isWeekend(dateStr)) return false;
    return true;
  });
};

/** Minimum covers per venue. Reynards requires 6+ every day; others have no minimum. */
const getMinCoversForVenue = (venueId) => {
  if (venueId === 'reynards') return 6;
  return 1;
};


// ─── BOOKING STATUS DEFINITIONS (8-stage flow) ───
const BOOKING_STATUSES = [
  { id: 'booked', label: 'Booked', short: 'Booked', color: 'bg-blue-50 border-blue-300 text-blue-900', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { id: 'deposit_confirmed', label: 'Deposit / Confirmed', short: 'Confirmed', color: 'bg-indigo-50 border-indigo-300 text-indigo-900', badge: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  { id: 'table_assigned', label: 'Table Assigned', short: 'Table', color: 'bg-purple-50 border-purple-300 text-purple-900', badge: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  { id: 'seated', label: 'Seated', short: 'Seated', color: 'bg-green-50 border-green-300 text-green-900', badge: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  { id: 'no_show', label: 'No Show', short: 'No Show', color: 'bg-red-50 border-red-300 text-red-900', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  { id: 'cancelled', label: 'Cancelled', short: 'Cancelled', color: 'bg-gray-50 border-gray-300 text-gray-600', badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  { id: 'completed', label: 'Completed', short: 'Done', color: 'bg-teal-50 border-teal-300 text-teal-900', badge: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500' },
  { id: 'waitlist', label: 'Waitlist', short: 'Waitlist', color: 'bg-amber-50 border-amber-300 text-amber-900', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  { id: 'linked_to_event', label: 'Linked to Event', short: 'Event', color: 'bg-purple-50 border-purple-200 text-purple-400', badge: 'bg-purple-100 text-purple-500', dot: 'bg-purple-400' },
];

const getStatusDef = (status) => BOOKING_STATUSES.find((s) => s.id === status) || BOOKING_STATUSES[0];

const getCapacityColor = (percentage) => {
  if (percentage >= 100) return 'bg-red-500';
  if (percentage >= 75) return 'bg-orange-500';
  if (percentage >= 50) return 'bg-amber-500';
  return 'bg-green-500';
};

const getStatusBadgeColor = (status) => getStatusDef(status).badge;

const timeInSitting = (time, sitting) => {
  if (!time) return false;
  const [hours, minutes] = time.split(':').map(Number);
  const totalMins = hours * 60 + (minutes || 0);
  const startMins = sitting.startH * 60;
  const endMins = sitting.endH * 60;
  return totalMins >= startMins && totalMins < endMins;
};

// ─── ROLLING TABLE-TURN CAPACITY ───────────────────────────────────────────
// Sittings (Breakfast/Lunch/Evening) are 3-5 hour windows, but a table only
// occupies covers for one ~2-hour turn, not the whole sitting. Capacity must
// reset as tables turn over, and must be checked against the venue's whole
// day (not just bookings whose start time falls in the same sitting) since a
// booking near a sitting boundary still occupies a table into the next one.
// NOTE: this same block is duplicated in public/booking-widget.html, which is
// plain static JS with no build step and can't import from src/. Keep both in
// sync if this logic ever changes.
const TURN_MINUTES = 120; // table turn time, uniform across venues

const timeToMins = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

// Bookings that actually occupy a table for this venue+date — excludes
// cancelled, no_show, and linked_to_event (already absorbed into an event;
// see "This booking no longer counts against restaurant capacity" below).
//
// Events held IN a restaurant area (e.g. a table booking converted to a
// private party that still sits in R&R) also take tables: each counts its
// guests for one normal 2-hour turn from its start time. Music nights are
// excluded — their guest figure is bar attendance, not seated diners.
// Mirrors the public_table_availability view (migration 024) the widget uses.
const EVENT_HOLD_EXCLUDED_STAGES = ['archived', 'lost', 'cancelled'];
// An event holds tables for its whole length: one 2-hour "turn" row every
// 120 min from start to end (17:00–23:00 → 17:00, 19:00, 21:00). No end time
// → one turn. No start time → held 09:00–23:00. Same rules as the
// public_table_availability view (migration 026).
const HHMM_RE = /^(\d{1,2}):(\d{2})/;
const eventHoldTimes = (e) => {
  const sm = HHMM_RE.exec(e.startTime || '');
  const s = sm ? (+sm[1]) * 60 + (+sm[2]) : 540;
  if (s >= 1440) return [];
  const em = HHMM_RE.exec(e.endTime || '');
  let last;
  if (!sm) last = 1379;
  else if (!em) last = s;
  else { const en = (+em[1]) * 60 + (+em[2]); last = en <= s ? 1439 : Math.min(en, 1440) - 1; }
  const out = [];
  for (let m = s; m <= last; m += TURN_MINUTES) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  return out;
};
const eventHoldsFor = (events, venueId, dateStr) => (events || [])
  .filter((e) => e.venue_id === venueId && e.type !== 'music_night' &&
    !EVENT_HOLD_EXCLUDED_STAGES.includes(e.stage) && e.date &&
    dateStr >= e.date && dateStr <= (e.endDate || e.date))
  .flatMap((e) => eventHoldTimes(e).map((t, i) => ({ id: `event-${e.id}-${i}`, isEventHold: true, venue: venueId, date: dateStr, time: t, covers: e.guests || 0, status: 'event' })));

const activeBookingsFor = (bookings, venueId, dateStr, events = []) => {
  return bookings.filter((b) =>
    b.venue === venueId && b.date === dateStr &&
    b.status !== 'cancelled' && b.status !== 'no_show' && b.status !== 'linked_to_event' && b.status !== 'waitlist'
  ).concat(eventHoldsFor(events, venueId, dateStr));
};

// Covers occupying tables at one instant (minutes from midnight) — sum of
// every booking whose [start, start+120) window contains that instant.
const coversAt = (activeBookings, instantMins, turnMinutes = TURN_MINUTES) => {
  return activeBookings.reduce((sum, b) => {
    const start = timeToMins(b.time);
    if (start == null) return sum;
    return (instantMins >= start && instantMins < start + turnMinutes) ? sum + (b.covers || 0) : sum;
  }, 0);
};

// Peak concurrent covers anywhere in [rangeStart, rangeEnd) — used for the
// sitting-level "X / Y covers" display bar. rangeStart itself is a candidate
// so spillover from an earlier sitting (e.g. a 15:30 lunch booking still
// running at 16:00) is counted.
const peakCoversInRange = (activeBookings, rangeStartMins, rangeEndMins, turnMinutes = TURN_MINUTES) => {
  const candidates = [rangeStartMins, ...activeBookings
    .map((b) => timeToMins(b.time))
    .filter((t) => t != null && t >= rangeStartMins && t < rangeEndMins)];
  return Math.max(0, ...candidates.map((c) => coversAt(activeBookings, c, turnMinutes)));
};

// Would a new booking (time, covers) push any instant in its own 2-hour
// window over capacity? Same check the public widget runs before letting a
// guest pick a time slot (public/booking-widget.html) — used here to WARN
// staff, not block them: the "Add Booking" form previously only checked a
// single booking against the venue's static capacity (e.g. "45 max"), with
// no awareness of what else was already booked at that time, so a staff
// member could double-book a slot with no warning at all.
const wouldExceedCapacity = (activeBookings, timeStr, covers, capacity, turnMinutes = TURN_MINUTES) => {
  const newStart = timeToMins(timeStr);
  if (newStart == null) return false;
  const newEnd = newStart + turnMinutes;
  const candidates = [newStart, ...activeBookings
    .map((b) => timeToMins(b.time))
    .filter((t) => t != null && t > newStart && t < newEnd)];
  return candidates.some((c) => coversAt(activeBookings, c, turnMinutes) + covers > capacity);
};

export default function Tables({ onNavigate, navigateAction, clearNavigateAction }) {
  const { tableBookings, addTableBooking, updateTableBooking, deleteTableBooking, linkBookingToEvent, unlinkBookingFromEvent, events, addEvent, clients, addClient, venues, payments, addPayment, deletePayment, restaurantPackages } = useAppContext();
  const [showPackageManager, setShowPackageManager] = useState(false);
  const { notifySuccess, notifyError, notifyInfo } = useNotify();

  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showRestaurantSheet, setShowRestaurantSheet] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  // Set when a save would push the venue over capacity for that time slot
  // (existing bookings' ~2-hour turns accounted for). This WARNS, it never
  // blocks — staff can always override (combined tables, a planned
  // overbook, etc.) by hitting Save again once the warning is showing.
  // Cleared whenever venue/date/time/covers/sitting change so a stale
  // override can't silently apply to a different booking.
  const [overbookWarning, setOverbookWarning] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [showLinkToEventPicker, setShowLinkToEventPicker] = useState(false);
  const [showUpgradeVenuePicker, setShowUpgradeVenuePicker] = useState(false);
  const [upgradeTargetVenue, setUpgradeTargetVenue] = useState('');
  const [tableView, setTableView] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState({ amount: '', payment_type: 'card', category: 'payment', notes: '' });

  // ─── VENUE DAY BLOCKS ────────────────────────────────────────────────────────
  const [venueBlocks, setVenueBlocks] = useState([]); // array of { id, venueId, date, sittingId, reason, blockType }
  const [blockModal, setBlockModal]   = useState(null); // { venueId, venueName } | null
  const [blockReason, setBlockReason] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);
  // blockChanges: per-sitting state set in the modal: 'closed' | 'open' | 'default'
  const [blockChanges, setBlockChanges] = useState({});
  // Stores the key fields of a booking at the moment edit mode opens, so we
  // can detect whether date / time / guest notes actually changed on submit.
  const editingOriginalRef = useRef(null);

  // ─── BOOKING CONFIRMATION EMAIL ──────────────────────────────────────────────
  const sendBookingConfirmationEmail = async (booking) => {
    if (!booking.email) return;
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseKey) return;

    const venueName = TABLE_VENUES.find((v) => v.id === booking.venue)?.name || booking.venue;
    const dateFormatted = (() => {
      try {
        return new Date(booking.date + 'T12:00:00').toLocaleDateString('en-IE', {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        });
      } catch { return booking.date; }
    })();
    const guestFirst = (booking.guestName || 'Guest').split(' ')[0];

    const notesRow = booking.specialRequirements
      ? `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;width:140px;">Your Notes</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${booking.specialRequirements}</td></tr>`
      : '';

    await fetch(`${supabaseUrl}/functions/v1/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseKey}` },
      body: JSON.stringify({
        to: booking.email,
        subject: `Your Table Booking — Errigal Country House Hotel — ${dateFormatted}`,
        replyTo: 'info@errigalhotel.com',
        html:
          `<h2 style="color:#1A5C3A;margin-bottom:8px;">Booking Confirmed</h2>` +
          `<p style="font-size:15px;color:#374151;margin-bottom:20px;">Dear ${guestFirst}, thank you for your reservation. We look forward to welcoming you!</p>` +
          `<table style="width:100%;border-collapse:collapse;font-size:14px;margin-bottom:20px;">` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;width:140px;">Date</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${dateFormatted}</td></tr>` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Time</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.time || 'TBD'}</td></tr>` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Guests</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.covers} covers</td></tr>` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Venue</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${venueName}</td></tr>` +
          notesRow +
          `</table>` +
          `<div style="background:#F0FAF4;border:1px solid #BBF7D0;border-radius:8px;padding:16px;margin-bottom:20px;">` +
          `<p style="font-size:14px;color:#1A5C3A;font-weight:600;margin-bottom:8px;">Need to make changes?</p>` +
          `<p style="font-size:13px;color:#374151;margin:0;">To amend your booking, please contact us directly:<br>` +
          `<strong>Phone:</strong> 049 555 6901<br>` +
          `<strong>Email:</strong> <a href="mailto:info@errigalhotel.com" style="color:#1A5C3A;">info@errigalhotel.com</a></p>` +
          `<p style="font-size:13px;color:#6b7280;margin:8px 0 0;">If you need to cancel, please call us at least 24 hours in advance.</p>` +
          `</div>` +
          `<p style="font-size:13px;color:#6b7280;margin:0;">Errigal Country House Hotel &bull; Cootehill, Co. Cavan &bull; 049 555 6901</p>`,
        text: `Booking confirmed: ${dateFormatted} at ${booking.time || 'TBD'}, ${booking.covers} covers at ${venueName}. To change or cancel, call 049 555 6901 or email info@errigalhotel.com`,
      }),
    }).catch((err) => console.warn('Confirmation email failed:', err));
  };

  // ─── BOOKING CANCELLATION EMAILS ─────────────────────────────────────────────
  // Sends two emails when a booking is cancelled: a polite confirmation to the
  // customer (if we have their email) and an internal notification to reception.
  const RECEPTION_EMAIL = 'info@errigalhotel.com';

  const sendBookingCancellationEmails = async (booking) => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseKey) return;

    const venueName = TABLE_VENUES.find((v) => v.id === booking.venue)?.name || booking.venue;
    const dateFormatted = (() => {
      try {
        return new Date(booking.date + 'T12:00:00').toLocaleDateString('en-IE', {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        });
      } catch { return booking.date; }
    })();
    const dateShort = (() => {
      try {
        return new Date(booking.date + 'T12:00:00').toLocaleDateString('en-IE', {
          weekday: 'short', day: 'numeric', month: 'short',
        });
      } catch { return booking.date; }
    })();
    const guestFirst = (booking.guestName || 'Guest').split(' ')[0];

    const sendMail = (payload) =>
      fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseKey}` },
        body: JSON.stringify(payload),
      });

    // 1) Customer email — only if we have an address (phone bookings may not)
    if (booking.email) {
      await sendMail({
        to: booking.email,
        subject: `Booking Cancelled — Errigal Country House Hotel — ${dateFormatted}`,
        replyTo: RECEPTION_EMAIL,
        html:
          `<h2 style="color:#1A5C3A;margin-bottom:8px;">Booking Cancelled</h2>` +
          `<p style="font-size:15px;color:#374151;margin-bottom:20px;">Dear ${guestFirst}, this is to confirm that your table booking has been cancelled.</p>` +
          `<table style="width:100%;border-collapse:collapse;font-size:14px;margin-bottom:20px;">` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;width:140px;">Date</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${dateFormatted}</td></tr>` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Time</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.time || 'TBD'}</td></tr>` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Guests</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${booking.covers} covers</td></tr>` +
          `<tr><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#6b7280;">Venue</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;">${venueName}</td></tr>` +
          `</table>` +
          `<div style="background:#F0FAF4;border:1px solid #BBF7D0;border-radius:8px;padding:16px;margin-bottom:20px;">` +
          `<p style="font-size:14px;color:#1A5C3A;font-weight:600;margin-bottom:8px;">Changed your mind?</p>` +
          `<p style="font-size:13px;color:#374151;margin:0;">We'd be delighted to welcome you another time. To rebook:<br>` +
          `<strong>Phone:</strong> 049 555 6901<br>` +
          `<strong>Email:</strong> <a href="mailto:info@errigalhotel.com" style="color:#1A5C3A;">info@errigalhotel.com</a></p>` +
          `</div>` +
          `<p style="font-size:13px;color:#6b7280;margin:0;">Errigal Country House Hotel &bull; Cootehill, Co. Cavan &bull; 049 555 6901</p>`,
        text: `Your table booking for ${dateFormatted} at ${booking.time || 'TBD'} (${booking.covers} covers, ${venueName}) has been cancelled. To rebook, call 049 555 6901 or email info@errigalhotel.com`,
      }).catch((err) => console.warn('Customer cancellation email failed:', err));
    }

    // 2) Reception notification — always sent
    const rowStyle = 'padding:6px 10px;border-bottom:1px solid #e5e7eb;';
    await sendMail({
      to: RECEPTION_EMAIL,
      subject: `CANCELLED — ${dateShort} ${booking.time || 'TBD'} — ${booking.covers} covers — ${booking.guestName || 'Unknown guest'} (${venueName})`,
      html:
        `<h2 style="color:#B91C1C;margin-bottom:8px;">Table Booking Cancelled</h2>` +
        `<table style="width:100%;border-collapse:collapse;font-size:14px;margin-bottom:16px;">` +
        `<tr><td style="${rowStyle}color:#6b7280;width:140px;">Guest</td><td style="${rowStyle}font-weight:600;">${booking.guestName || '—'}</td></tr>` +
        `<tr><td style="${rowStyle}color:#6b7280;">Phone</td><td style="${rowStyle}">${booking.phone || '—'}</td></tr>` +
        `<tr><td style="${rowStyle}color:#6b7280;">Email</td><td style="${rowStyle}">${booking.email || '—'}</td></tr>` +
        `<tr><td style="${rowStyle}color:#6b7280;">Date</td><td style="${rowStyle}font-weight:600;">${dateFormatted}</td></tr>` +
        `<tr><td style="${rowStyle}color:#6b7280;">Time</td><td style="${rowStyle}font-weight:600;">${booking.time || 'TBD'}</td></tr>` +
        `<tr><td style="${rowStyle}color:#6b7280;">Covers</td><td style="${rowStyle}font-weight:600;">${booking.covers}</td></tr>` +
        `<tr><td style="${rowStyle}color:#6b7280;">Venue</td><td style="${rowStyle}">${venueName}</td></tr>` +
        (booking.tableNumber ? `<tr><td style="${rowStyle}color:#6b7280;">Table</td><td style="${rowStyle}">${booking.tableNumber}</td></tr>` : '') +
        (booking.notes ? `<tr><td style="${rowStyle}color:#6b7280;">Notes</td><td style="${rowStyle}">${booking.notes}</td></tr>` : '') +
        `</table>` +
        `<p style="font-size:13px;color:#6b7280;margin:0;">${booking.email ? 'The customer has been emailed a cancellation confirmation.' : 'No email on file — the customer has NOT been notified by email.'}</p>`,
      text: `Cancelled: ${booking.guestName || 'Unknown'} — ${dateFormatted} at ${booking.time || 'TBD'}, ${booking.covers} covers, ${venueName}. Phone: ${booking.phone || 'n/a'}`,
    }).catch((err) => console.warn('Reception cancellation email failed:', err));
  };

  // Reload blocks whenever the selected date changes
  useEffect(() => {
    fetchVenueBlocksByDates([selectedDate])
      .then(setVenueBlocks)
      .catch(() => {});
  }, [selectedDate]);

  /** All block records for a venue on the selected date. */
  const getBlocksForVenue = (venueId) =>
    venueBlocks.filter((b) => b.venueId === venueId && b.date === selectedDate);

  /** Opens the block-management modal, pre-populating current state from DB. */
  const openBlockModal = (venue) => {
    const existing = getBlocksForVenue(venue.id);
    const initial = { all_day: 'default', breakfast: 'default', lunch: 'default', evening: 'default' };
    existing.forEach((b) => { initial[b.sittingId] = b.blockType; });
    setBlockChanges(initial);
    setBlockReason(existing.find((b) => b.reason)?.reason || '');
    setBlockModal({ venueId: venue.id, venueName: venue.name });
  };

  /** Saves all sitting changes from the block-management modal in one batch. */
  const handleSaveBlocks = async () => {
    if (!blockModal) return;
    setBlockSaving(true);
    const sittingIds = ['all_day', 'breakfast', 'lunch', 'evening'];
    const existing = getBlocksForVenue(blockModal.venueId);
    try {
      await Promise.allSettled(
        sittingIds.map(async (sittingId) => {
          const desired = blockChanges[sittingId] || 'default';
          const existingBlock = existing.find((b) => b.sittingId === sittingId);
          if (desired === 'default') {
            if (existingBlock) await deleteVenueBlock(existingBlock.id);
          } else if (!existingBlock || existingBlock.blockType !== desired) {
            await insertVenueBlock({
              venueId: blockModal.venueId,
              date: selectedDate,
              sittingId,
              reason: blockReason.trim(),
              createdBy: '',
              blockType: desired,
            });
          }
        })
      );
      const fresh = await fetchVenueBlocksByDates([selectedDate]);
      setVenueBlocks(fresh);
      notifySuccess(`${blockModal.venueName} sittings updated`);
    } catch (e) {
      notifyError('Failed to save blocks: ' + (e.message || e));
    } finally {
      setBlockSaving(false);
      setBlockModal(null);
      setBlockReason('');
    }
  };

  const QUARTER_MINS = ['00','15','30','45'];
  const QUICK_TAGS = ['Gluten Free','Dairy Free','Vegan','Vegetarian','Nut Allergy','Coeliac','Halal','Birthday','Anniversary','Communion','Christening','High Chair'];

  const [formData, setFormData] = useState({
    guestName: '',
    phone: '',
    email: '',
    venue: '',
    sitting: '',
    time: '',
    date: selectedDate,
    covers: '2',
    source: 'web',
    roomNumber: '',
    status: 'booked',
    tableNumber: '',
    notes: '',
    specialRequirements: '',
    packageName: '',
  });

  // Pre-fill the booking form when arriving via Email page's
  // "New Booking" button (navigateAction.action === 'create-from-email').
  useEffect(() => {
    if (!navigateAction || typeof navigateAction !== 'object') return;
    if (navigateAction.action === 'create-for-date' && navigateAction.date) {
      // Home page "New booking" — open the form on the day being viewed
      setSelectedDate(navigateAction.date);
      setFormData((prev) => ({ ...prev, date: navigateAction.date }));
      setShowBookingForm(true);
      clearNavigateAction?.();
      return;
    }
    if (navigateAction.action !== 'create-from-email') return;
    setFormData((prev) => ({
      ...prev,
      guestName: navigateAction.guestName || '',
      email: navigateAction.email || '',
      notes: navigateAction.notes || '',
    }));
    setShowBookingForm(true);
    clearNavigateAction?.();
  }, [navigateAction, clearNavigateAction]);

  const bookingsForDate = useMemo(() => {
    return (tableBookings || []).filter((b) => b.date === selectedDate);
  }, [tableBookings, selectedDate]);

  const getValidSittingsForVenue = (venueId, dateStr = selectedDate) => {
    const blocks = venueBlocks.filter((b) => b.venueId === venueId && b.date === dateStr);
    return getSittingsForVenueAndDate(venueId, dateStr, blocks);
  };

  const getBookingsForCell = (venueId, sittingId) => {
    const sitting = SITTINGS.find((s) => s.id === sittingId);
    if (!sitting) return [];
    return bookingsForDate.filter(
      (b) => b.venue === venueId && timeInSitting(b.time, sitting) && b.status !== 'linked_to_event'
    );
  };

  // All bookings for a cell including linked (for display purposes)
  const getAllBookingsForCell = (venueId, sittingId) => {
    const sitting = SITTINGS.find((s) => s.id === sittingId);
    if (!sitting) return [];
    return bookingsForDate.filter(
      (b) => b.venue === venueId && timeInSitting(b.time, sitting)
    );
  };

  // Peak concurrent covers for this venue during this sitting's window,
  // accounting for the ~2-hour table turn — NOT a flat sum of every booking
  // whose start time falls in the sitting (that treated the whole 3-5 hour
  // sitting as one non-renewing pool, e.g. a full 12:00 lunch would wrongly
  // block a 14:00 booking after tables had already turned). Checked against
  // the venue's whole day so spillover from an adjacent sitting still counts.
  const getTotalCoversForCell = (venueId, sittingId) => {
    const sitting = SITTINGS.find((s) => s.id === sittingId);
    if (!sitting) return 0;
    const active = activeBookingsFor(bookingsForDate, venueId, selectedDate, events);
    return peakCoversInRange(active, sitting.startH * 60, sitting.endH * 60);
  };

  // Covers in use at each half hour of the sitting (2-hour turn), so staff can
  // see WHEN a sitting is busy — the single peak figure made a sitting look
  // nearly full all evening when it was only tight around one time.
  const getHalfHourLoad = (venueId, sittingId) => {
    const sitting = SITTINGS.find((s) => s.id === sittingId);
    if (!sitting) return [];
    const active = activeBookingsFor(bookingsForDate, venueId, selectedDate, events);
    const out = [];
    for (let m = sitting.startH * 60; m < sitting.endH * 60; m += 30) {
      out.push({ mins: m, label: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`, covers: coversAt(active, m) });
    }
    return out;
  };

  const getVenueCapacityForSlot = (venueId, sittingId) => {
    return getVenueCapacityForDate(venueId, selectedDate, sittingId);
  };

  const getCapacityPercentage = (venueId, sittingId) => {
    const covers = getTotalCoversForCell(venueId, sittingId);
    const capacity = getVenueCapacityForSlot(venueId, sittingId);
    return capacity > 0 ? (covers / capacity) * 100 : 0;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Any field that changes what we'd be checking capacity against
    // invalidates a pending overbook override — don't let a "book anyway"
    // from a moment ago silently apply to different details.
    if (['venue', 'date', 'time', 'covers', 'sitting'].includes(name)) {
      setOverbookWarning(null);
    }

    if (name === 'venue') {
      const validSittings = getValidSittingsForVenue(value);
      const newSitting = validSittings.length > 0 ? validSittings[0].id : '';
      setFormData((prev) => ({
        ...prev,
        sitting: newSitting,
        time: '',
      }));
    }

    if (name === 'sitting') {
      setFormData((prev) => ({
        ...prev,
        time: '',
      }));
    }
  };

  const getTimeRangeForSitting = (sittingId) => {
    const sitting = SITTINGS.find((s) => s.id === sittingId);
    if (!sitting) return { start: '09:00', end: '12:00' };
    const start = `${String(sitting.startH).padStart(2, '0')}:00`;
    const end = `${String(sitting.endH).padStart(2, '0')}:00`;
    return { start, end };
  };

  const validateForm = () => {
    if (!formData.guestName.trim()) return 'Guest name is required';
    if (!formData.venue) return 'Venue is required';
    if (!formData.sitting) return 'Sitting is required';
    const targetDate = formData.date || selectedDate;
    // Check the sitting is actually valid for this venue + date
    const validSittings = getValidSittingsForVenue(formData.venue, targetDate);
    if (!validSittings.some(s => s.id === formData.sitting)) {
      const venueName = TABLE_VENUES.find(v => v.id === formData.venue)?.name || formData.venue;
      return `${venueName} is not open for ${formData.sitting} on this date`;
    }
    if (!formData.time) return 'Time is required';
    // Check the time falls within the selected sitting
    const selectedSitting = SITTINGS.find(s => s.id === formData.sitting);
    if (selectedSitting) {
      const [h, m] = formData.time.split(':').map(Number);
      const timeMins = h * 60 + (m || 0);
      const startMins = selectedSitting.startH * 60;
      const endMins = selectedSitting.endH * 60;
      if (timeMins < startMins || timeMins >= endMins) {
        return `Time ${formData.time} is outside the ${selectedSitting.label} sitting (${selectedSitting.time})`;
      }
    }
    const venueCapacity = getVenueCapacityForDate(formData.venue, formData.date || selectedDate, formData.sitting) || 60;
    if (!formData.covers || formData.covers < 1 || formData.covers > venueCapacity) return `Covers must be between 1 and ${venueCapacity}`;
    const minCovers = getMinCoversForVenue(formData.venue);
    if (parseInt(formData.covers) < minCovers) return `Reynards requires a minimum of ${minCovers} covers`;
    if (!formData.phone.trim()) return 'Phone is required';
    if (!formData.email.trim()) return 'Email is required';
    return null;
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      notifyError(error);
      return;
    }

    const covers = parseInt(formData.covers);
    const bookingDate = formData.date || selectedDate;

    // Soft capacity check: does this booking push the venue over capacity
    // for this time slot once other bookings' ~2-hour turns are accounted
    // for? This never blocks the save — staff can always override
    // (combined tables, a planned overbook, etc.) by hitting Save again —
    // it just makes sure they see it coming instead of finding out when a
    // guest turns up with nowhere to sit.
    if (!overbookWarning) {
      const venueCapacity = getVenueCapacityForDate(formData.venue, bookingDate, formData.sitting) || 60;
      const activeForCheck = activeBookingsFor(tableBookings || [], formData.venue, bookingDate, events)
        .filter((b) => !(isEditMode && b.id === editingBookingId));
      if (wouldExceedCapacity(activeForCheck, formData.time, covers, venueCapacity)) {
        const venueName = TABLE_VENUES.find((v) => v.id === formData.venue)?.name || formData.venue;
        const startMins = timeToMins(formData.time);
        const occupiedNearby = startMins == null
          ? 0
          : peakCoversInRange(activeForCheck, startMins, startMins + TURN_MINUTES);
        const msg = `${venueName} already has ${occupiedNearby} covers committed around ${formData.time} — adding ${covers} more would push it over its ${venueCapacity}-cover capacity. Click Save again to book anyway.`;
        setOverbookWarning(msg);
        notifyError(msg, { duration: 0 });
        return;
      }
    }

    const bookingData = {
      id: isEditMode ? editingBookingId : `booking-${Date.now()}`,
      guestName: formData.guestName,
      phone: formData.phone,
      email: formData.email,
      venue: formData.venue,
      date: bookingDate,
      time: formData.time,
      covers,
      source: formData.source,
      roomNumber: formData.roomNumber,
      status: formData.status,
      tableNumber: formData.tableNumber,
      notes: formData.notes,
      specialRequirements: formData.specialRequirements,
      packageName: formData.packageName || '',
    };

    if (isEditMode) {
      try {
        await updateTableBooking(bookingData.id, bookingData);
        setBookingSuccess(`Booking updated for ${formData.guestName}`);
        // If the booking's date changed, jump the view to the new date
        if (bookingDate !== selectedDate) {
          setSelectedDate(bookingDate);
        }
        const orig = editingOriginalRef.current || {};
        const newlyCancelled = formData.status === 'cancelled' && orig.status !== 'cancelled';
        if (newlyCancelled) {
          // Notify customer + reception; skip the normal confirmation email
          sendBookingCancellationEmails(bookingData).catch(() => {});
        } else if (formData.email) {
          // Auto-send updated confirmation if date, time, or guest notes changed
          const changed =
            (formData.date || '') !== (orig.date || '') ||
            (formData.time || '') !== (orig.time || '') ||
            (formData.specialRequirements || '') !== (orig.specialRequirements || '') ||
            String(formData.covers || '') !== String(orig.covers || '');
          if (changed) {
            sendBookingConfirmationEmail({ ...bookingData, email: formData.email }).catch(() => {});
          }
        }
        editingOriginalRef.current = null;
      } catch (err) {
        notifyError(`Failed to update booking: ${err.message}`);
        return;
      }
    } else {
      try {
        const result = await addTableBooking(bookingData);
        if (result?._dbError) {
          // Nothing was saved. Keep the form open with everything the user
          // typed still in it, and keep the error on screen until it's
          // dismissed — a toast that vanishes after a few seconds during a
          // busy service is exactly how this got missed before.
          notifyError(
            `Could not save this booking — nothing has been recorded: ${result._dbError}\n\nPlease try Save again before closing this form.`,
            { duration: 0 }
          );
          return;
        }
        setBookingSuccess(`Booking confirmed for ${formData.guestName} — ${covers} covers on ${bookingDate} at ${formData.time}`);
        if (bookingDate !== selectedDate) {
          setSelectedDate(bookingDate);
        }
        // Send guest confirmation email if email address provided
        if (formData.email) {
          sendBookingConfirmationEmail({ ...bookingData, email: formData.email }).catch(() => {});
        }
      } catch (err) {
        notifyError(`Failed to create booking: ${err.message}`, { duration: 0 });
        return;
      }
    }
    setTimeout(() => setBookingSuccess(null), 4000);

    setShowBookingForm(false);
    setIsEditMode(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedHour(null);
    setEditingBookingId(null);
    setOverbookWarning(null);
    setFormData({
      guestName: '',
      phone: '',
      email: '',
      venue: TABLE_VENUES[0].id,
      sitting: SITTINGS.filter((s) => s.venues.includes(TABLE_VENUES[0].id))[0]?.id || '',
      time: '',
      date: selectedDate,
      covers: '2',
      source: 'web',
      roomNumber: '',
      status: 'booked',
      tableNumber: '',
      notes: '',
      specialRequirements: '',
      packageName: '',
    });
  };

  const handleAddBookingForSlot = (venueId, sittingId) => {
    setIsEditMode(false);
    resetForm();
    setFormData((prev) => ({
      ...prev,
      venue: venueId,
      sitting: sittingId,
    }));
    setShowBookingForm(true);
  };

  const handleAddBooking = () => {
    setIsEditMode(false);
    resetForm();
    setShowBookingForm(true);
  };

  const handleEditBooking = () => {
    if (!selectedBooking) return;
    // Snapshot the fields we care about before the user edits them
    editingOriginalRef.current = {
      date: selectedBooking.date || '',
      time: selectedBooking.time || '',
      specialRequirements: selectedBooking.specialRequirements || '',
      covers: String(selectedBooking.covers || ''),
      status: selectedBooking.status || '',
    };
    setIsEditMode(true);
    setEditingBookingId(selectedBooking.id);
    const editTime = selectedBooking.time || '';
    const editHour = editTime ? parseInt(editTime.split(':')[0]) : null;
    setSelectedHour(editHour);
    setFormData({
      guestName: selectedBooking.guestName,
      phone: selectedBooking.phone,
      email: selectedBooking.email,
      venue: selectedBooking.venue,
      sitting: SITTINGS.find((s) => timeInSitting(selectedBooking.time, s))?.id || '',
      time: editTime,
      date: selectedBooking.date || selectedDate,
      covers: String(selectedBooking.covers),
      source: selectedBooking.source,
      roomNumber: selectedBooking.roomNumber || '',
      status: selectedBooking.status,
      tableNumber: selectedBooking.tableNumber || '',
      notes: selectedBooking.notes || '',
      specialRequirements: selectedBooking.specialRequirements || '',
      packageName: selectedBooking.packageName || '',
    });
    setSelectedBooking(null);
    setShowBookingForm(true);
  };

  const handleCancelEdit = () => {
    setShowBookingForm(false);
    setIsEditMode(false);
    resetForm();
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking) return;
    const bookingId = selectedBooking.id;
    setSelectedBooking(null);
    setShowDeleteConfirm(false);
    try {
      await deleteTableBooking(bookingId);
    } catch (err) {
      notifyError('Failed to delete booking: ' + (err?.message || 'unknown error') + ' — it has been restored.');
    }
  };

  // Payment recording for table bookings
  const bookingPayments = useMemo(() => {
    if (!selectedBooking) return [];
    return payments.filter(p => p.booking_id === selectedBooking.id || p.bookingId === selectedBooking.id);
  }, [payments, selectedBooking]);

  const handleDeletePayment = async (pmt) => {
    if (!pmt || !pmt.id) return;
    const amt = parseFloat(pmt.amount || 0).toFixed(2);
    const when = pmt.payment_date || pmt.paymentDate || '';
    if (!window.confirm(`Delete payment €${amt}${when ? ' on ' + when : ''}?\n\nThis cannot be undone.`)) return;
    try {
      await deletePayment(pmt.id);
      notifySuccess('Payment deleted');
    } catch (err) {
      notifyError('Failed to delete payment: ' + (err?.message || 'unknown error'));
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedBooking || !paymentData.amount) return;
    const amount = parseFloat(paymentData.amount);
    if (isNaN(amount) || amount <= 0) return;

    try {
      // Note: appPaymentToDb maps `paymentType` → DB `payment_type` and
      // `description` → DB `description`. Passing snake_case would be dropped.
      const saved = await addPayment({
        booking_id: selectedBooking.id,
        amount,
        paymentType: paymentData.payment_type,
        category: paymentData.category,
        description: paymentData.notes || `Payment for booking - ${selectedBooking.guestName}`,
        payment_date: todayKey(),
      });
      if (saved && typeof saved.id === 'string' && saved.id.startsWith('temp_')) {
        notifyError('Payment was not saved to the database. Check your connection and try again.');
        return;
      }
      notifySuccess('Payment recorded');
    } catch (err) {
      notifyError('Failed to save payment: ' + (err?.message || 'unknown error'));
      return;
    }

    // Auto-mark deposit as paid if this is a deposit payment. The payment
    // itself already saved successfully above — a failure here only means
    // the depositPaid flag didn't stick, so it's surfaced but doesn't undo
    // the payment record.
    if (paymentData.category === 'deposit' && !selectedBooking.depositPaid) {
      try {
        await updateTableBooking(selectedBooking.id, { depositPaid: true });
        setSelectedBooking(prev => ({ ...prev, depositPaid: true }));
      } catch (err) {
        notifyError('Payment recorded, but failed to mark deposit as paid: ' + (err?.message || 'unknown error'));
      }
    }

    setPaymentData({ amount: '', payment_type: 'card', category: 'payment', notes: '' });
    setShowPaymentForm(false);
    setBookingSuccess('Payment recorded successfully');
    setTimeout(() => setBookingSuccess(null), 3000);
  };

  const handleQuickStatusChange = useCallback(async (newStatus) => {
    if (!selectedBooking) return;
    const updates = { status: newStatus };
    if (newStatus === 'seated') updates.seatedAt = new Date().toISOString();
    if (newStatus === 'completed') updates.completedAt = new Date().toISOString();
    const previousBooking = selectedBooking;
    setSelectedBooking((prev) => ({ ...prev, ...updates }));
    try {
      await updateTableBooking(selectedBooking.id, updates);
    } catch (err) {
      setSelectedBooking(previousBooking);
      notifyError('Failed to update status: ' + (err?.message || 'unknown error'));
      return;
    }
    // Notify customer + reception only once the status change is confirmed
    // saved — otherwise a failed save could still have emailed the guest
    // that their booking was cancelled when it never actually was.
    if (newStatus === 'cancelled' && previousBooking.status !== 'cancelled') {
      sendBookingCancellationEmails(previousBooking).catch(() => {});
    }
  }, [selectedBooking, updateTableBooking, notifyError]);

  const handleTableNumberChange = useCallback(async (tableNum) => {
    if (!selectedBooking) return;
    const updates = { tableNumber: tableNum };
    if (tableNum && selectedBooking.status === 'booked') {
      updates.status = 'table_assigned';
    }
    const previousBooking = selectedBooking;
    setSelectedBooking((prev) => ({ ...prev, ...updates }));
    try {
      await updateTableBooking(selectedBooking.id, updates);
    } catch (err) {
      setSelectedBooking(previousBooking);
      notifyError('Failed to update table number: ' + (err?.message || 'unknown error'));
    }
  }, [selectedBooking, updateTableBooking, notifyError]);

  const handleNotesChange = useCallback(async (notes) => {
    if (!selectedBooking) return;
    const previousBooking = selectedBooking;
    setSelectedBooking((prev) => ({ ...prev, notes }));
    try {
      await updateTableBooking(selectedBooking.id, { notes });
    } catch (err) {
      setSelectedBooking(previousBooking);
      notifyError('Failed to save notes: ' + (err?.message || 'unknown error'));
    }
  }, [selectedBooking, updateTableBooking, notifyError]);

  const handleSpecialRequirementsChange = useCallback(async (specialRequirements) => {
    if (!selectedBooking) return;
    const previousBooking = selectedBooking;
    setSelectedBooking((prev) => ({ ...prev, specialRequirements }));
    try {
      await updateTableBooking(selectedBooking.id, { specialRequirements });
    } catch (err) {
      setSelectedBooking(previousBooking);
      notifyError('Failed to save special requirements: ' + (err?.message || 'unknown error'));
    }
  }, [selectedBooking, updateTableBooking, notifyError]);

  const handleCreateClientFromBooking = useCallback(async () => {
    if (!selectedBooking) return;
    const existingClient = (clients || []).find(
      (c) => c.email && c.email.toLowerCase() === selectedBooking.email?.toLowerCase()
    );
    if (existingClient) {
      notifyInfo(`Client already exists: ${existingClient.name}`);
      return;
    }
    if (!selectedBooking.email || !selectedBooking.guestName) {
      notifyError('Booking must have a name and email to create a client');
      return;
    }
    try {
      await addClient({
        name: selectedBooking.guestName,
        email: selectedBooking.email,
        phone: selectedBooking.phone || '',
        type: 'individual',
        source: 'table_booking',
        notes: `Auto-created from table booking on ${selectedBooking.date}`,
      });
      notifySuccess(`Client "${selectedBooking.guestName}" created successfully!`);
    } catch (err) {
      notifyError('Failed to create client: ' + err.message);
    }
  }, [selectedBooking, clients, addClient]);

  // ── Upgrade to Event: show venue picker, then create event and link booking ──
  const handleUpgradeToEvent = useCallback(() => {
    if (!selectedBooking) return;
    setUpgradeTargetVenue('');
    setShowUpgradeVenuePicker(true);
  }, [selectedBooking]);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!selectedBooking || !upgradeTargetVenue) return;
    const sitting = SITTINGS.find((s) => timeInSitting(selectedBooking.time, s));
    const endH = sitting ? sitting.endH : (parseInt((selectedBooking.time || '18:00').split(':')[0]) + 3);
    const endTime = `${String(endH).padStart(2, '0')}:00`;

    // Build the richest set of notes we can from table-booking fields so the
    // Function Sheet has meaningful content the moment it's opened — rather
    // than a blank event that only shows the basics. The operator can still
    // refine everything afterwards, but nothing is silently dropped.
    const noteParts = ['Upgraded from table booking.'];
    if (selectedBooking.partyComposition) noteParts.push(`Party: ${selectedBooking.partyComposition}`);
    if (selectedBooking.roomNumber) noteParts.push(`Hotel room: ${selectedBooking.roomNumber}`);
    if (selectedBooking.tableNumber) noteParts.push(`Table: ${selectedBooking.tableNumber}`);
    if (selectedBooking.notes) noteParts.push(selectedBooking.notes);
    const notes = noteParts.join('\n');

    try {
      const newEvent = await addEvent({
        name: `${selectedBooking.guestName} (from table)`,
        client: selectedBooking.guestName,
        clientPhone: selectedBooking.phone || '',
        clientEmail: selectedBooking.email || '',
        date: selectedBooking.date,
        venue_id: upgradeTargetVenue,
        guests: selectedBooking.covers || 0,
        stage: 'confirmed',
        startTime: selectedBooking.time || '18:00',
        endTime,
        type: 'private_party',
        setup: 'banquet',
        // Carry across every operator-meaningful text field so the
        // Function Sheet stays in sync with what the booking had.
        setupRequirements: selectedBooking.specialRequirements || '',
        notes,
        depositPaid: selectedBooking.depositPaid || false,
      });
      await linkBookingToEvent(selectedBooking.id, newEvent.id);
      setSelectedBooking((prev) => ({ ...prev, linkedEventId: newEvent.id, status: 'linked_to_event' }));

      // If the table booking had a paid deposit, record it as a payment against
      // the new event so balance / invoice totals stay accurate from day one.
      if (selectedBooking.depositPaid && (selectedBooking.depositAmount || 0) > 0) {
        try {
          await addPayment({
            eventId: newEvent.id,
            amount: selectedBooking.depositAmount,
            paymentType: selectedBooking.stripePaymentId ? 'card' : 'bank_transfer',
            category: 'deposit',
            description: `Deposit carried from table booking ${selectedBooking.id}`,
            payment_date: selectedBooking.date || undefined,
          });
        } catch (e) {
          console.warn('Could not migrate deposit payment:', e);
        }
      }

      setShowUpgradeVenuePicker(false);
      const venueName = (venues || []).find((v) => v.id === upgradeTargetVenue)?.name || upgradeTargetVenue;
      setBookingSuccess(`Booking upgraded to event in ${venueName}!`);
      setTimeout(() => setBookingSuccess(null), 5000);
    } catch (err) {
      notifyError('Failed to create event: ' + err.message);
    }
  }, [selectedBooking, upgradeTargetVenue, addEvent, addPayment, linkBookingToEvent, venues]);

  // ── Link booking to an existing event ──
  const handleLinkToExistingEvent = useCallback(async (eventId) => {
    if (!selectedBooking) return;
    try {
      await linkBookingToEvent(selectedBooking.id, eventId);
    } catch (err) {
      notifyError('Failed to link booking to event: ' + (err?.message || 'unknown error'));
      return;
    }
    setSelectedBooking((prev) => ({ ...prev, linkedEventId: eventId, status: 'linked_to_event' }));
    setShowLinkToEventPicker(false);
    setBookingSuccess('Booking linked to event successfully!');
    setTimeout(() => setBookingSuccess(null), 4000);
  }, [selectedBooking, linkBookingToEvent, notifyError]);

  // ── Unlink booking from event (revert to table) ──
  const handleUnlinkFromEvent = useCallback(async () => {
    if (!selectedBooking) return;
    try {
      await unlinkBookingFromEvent(selectedBooking.id, 'booked');
    } catch (err) {
      notifyError('Failed to unlink booking from event: ' + (err?.message || 'unknown error'));
      return;
    }
    setSelectedBooking((prev) => ({ ...prev, linkedEventId: null, status: 'booked' }));
    setBookingSuccess('Booking unlinked — restored as active table booking.');
    setTimeout(() => setBookingSuccess(null), 4000);
  }, [selectedBooking, unlinkBookingFromEvent, notifyError]);

  // Get events on the same date as selected booking (for "Link to Event" picker)
  const eventsOnBookingDate = useMemo(() => {
    if (!selectedBooking) return [];
    return (events || []).filter((e) => e.date === selectedBooking.date);
  }, [events, selectedBooking]);

  if (selectedBooking && !showBookingForm) {
    const currentStatusDef = getStatusDef(selectedBooking.status);
    const clientExists = (clients || []).some(
      (c) => c.email && c.email.toLowerCase() === selectedBooking.email?.toLowerCase()
    );

    return (
      <div className="p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <button
          onClick={() => setSelectedBooking(null)}
          className="mb-6 flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
        >
          <ArrowLeft size={18} /> Back to Grid
        </button>

        <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 max-w-3xl">
          {/* Header with name & actions */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{selectedBooking.guestName}</h1>
              <div className="flex items-center gap-2 mt-2">
                <span className={`w-2.5 h-2.5 rounded-full ${currentStatusDef.dot}`}></span>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${currentStatusDef.badge}`}>
                  {currentStatusDef.label}
                </span>
                {selectedBooking.tableNumber && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    <Hash size={12} /> {selectedBooking.tableNumber}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {!clientExists && selectedBooking.email && (
                <button
                  onClick={handleCreateClientFromBooking}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 h-10 rounded-lg font-medium text-sm"
                  title="Add this guest as a client for marketing"
                >
                  <UserPlus size={16} /> Add as Client
                </button>
              )}
              {!selectedBooking.linkedEventId && (
                <>
                  <button
                    onClick={handleUpgradeToEvent}
                    className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 h-10 rounded-lg font-medium text-sm"
                    title="Create a new event from this booking"
                  >
                    <ArrowUpRight size={16} /> Upgrade to Event
                  </button>
                  {eventsOnBookingDate.length > 0 && (
                    <button
                      onClick={() => setShowLinkToEventPicker(true)}
                      className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 h-10 rounded-lg font-medium text-sm"
                      title="Link this booking to an existing event"
                    >
                      <Link2 size={16} /> Add to Event
                    </button>
                  )}
                </>
              )}
              {selectedBooking.linkedEventId && (
                <button
                  onClick={handleUnlinkFromEvent}
                  className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 h-10 rounded-lg font-medium text-sm"
                  title="Unlink from event and restore as table booking"
                >
                  <Unlink size={16} /> Revert to Table
                </button>
              )}
              <button
                onClick={handleEditBooking}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 h-10 rounded-lg font-medium text-sm"
              >
                <Edit size={16} /> Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 h-10 rounded-lg font-medium text-sm"
              >
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </div>

          {/* Linked to Event Banner */}
          {selectedBooking.linkedEventId && (() => {
            const linkedEvent = (events || []).find((e) => e.id === selectedBooking.linkedEventId);
            return (
              <div className="mb-6 bg-purple-50 border-2 border-purple-300 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                    <Link2 size={16} /> Linked to Event
                  </p>
                  <p className="text-sm text-purple-700 mt-1">
                    {linkedEvent ? `${linkedEvent.name} — ${(venues || []).find((v) => v.id === linkedEvent.venue_id)?.name || 'No venue'}` : 'Event not found'}
                  </p>
                  <p className="text-xs text-purple-500 mt-0.5">
                    {linkedEvent && TABLE_VENUES.some((tv) => tv.id === linkedEvent.venue_id)
                      ? `The event is held in ${TABLE_VENUES.find((tv) => tv.id === linkedEvent.venue_id).name}, so its ${linkedEvent.guests || 0} guests still count against ${TABLE_VENUES.find((tv) => tv.id === linkedEvent.venue_id).name} capacity ${linkedEvent.startTime ? `from ${String(linkedEvent.startTime).slice(0, 5)}${linkedEvent.endTime ? ` to ${String(linkedEvent.endTime).slice(0, 5)}` : ' for 2 hours'}` : 'all day (no start time set)'}`
                      : 'The event is in a function room, so this booking no longer uses restaurant capacity'}
                  </p>
                </div>
                {linkedEvent && (
                  <button
                    onClick={() => onNavigate('diary', { scrollToEvent: linkedEvent.id })}
                    className="flex items-center gap-1 text-sm font-medium text-purple-700 hover:text-purple-900 bg-purple-100 hover:bg-purple-200 px-3 py-1.5 rounded-lg transition"
                  >
                    Open event <ChevronRight size={14} />
                  </button>
                )}
              </div>
            );
          })()}

          {/* Link to Existing Event Picker */}
          {showLinkToEventPicker && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full max-h-[70vh] flex flex-col">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Link to Existing Event</h3>
                <p className="text-sm text-gray-500 mb-4">Select an event on {selectedBooking.date} to link this booking to:</p>
                <div className="overflow-y-auto flex-1 space-y-2 mb-4">
                  {eventsOnBookingDate.length > 0 ? eventsOnBookingDate.map((evt) => (
                    <button
                      key={evt.id}
                      onClick={() => handleLinkToExistingEvent(evt.id)}
                      className="w-full text-left p-3 bg-gray-50 hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 rounded-lg transition"
                    >
                      <p className="text-sm font-medium text-gray-900">{evt.name}</p>
                      <p className="text-xs text-gray-500">
                        {(venues || []).find((v) => v.id === evt.venue_id)?.name || 'No venue'} • {evt.startTime || ''} – {evt.endTime || ''} • {evt.guests || 0} guests
                      </p>
                    </button>
                  )) : (
                    <p className="text-sm text-gray-500 italic">No events on this date</p>
                  )}
                </div>
                <button
                  onClick={() => setShowLinkToEventPicker(false)}
                  className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 font-medium text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Upgrade to Event — Venue Picker */}
          {showUpgradeVenuePicker && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Upgrade to Event</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Moving <strong>{selectedBooking.guestName}</strong> ({selectedBooking.covers} covers) from table service to a function event. Choose the venue:
                </p>
                <select
                  value={upgradeTargetVenue}
                  onChange={(e) => setUpgradeTargetVenue(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a venue...</option>
                  {(venues || []).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} {v.capacity_banquet ? `(up to ${v.capacity_banquet} banquet)` : ''}
                    </option>
                  ))}
                </select>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowUpgradeVenuePicker(false)}
                    className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 font-medium text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmUpgrade}
                    disabled={!upgradeTargetVenue}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium text-sm transition"
                  >
                    Confirm Upgrade
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quick Status Actions */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h2 className="text-sm font-semibold text-gray-700 uppercase mb-3">Quick Status Change</h2>
            <div className="flex flex-wrap gap-2">
              {BOOKING_STATUSES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleQuickStatusChange(s.id)}
                  disabled={selectedBooking.status === s.id}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${
                    selectedBooking.status === s.id
                      ? `${s.color} ring-2 ring-offset-1 ring-gray-400 cursor-default`
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Table Number Assignment */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h2 className="text-sm font-semibold text-gray-700 uppercase mb-3 flex items-center gap-2">
              <Hash size={14} /> Table Assignment
            </h2>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={selectedBooking.tableNumber || ''}
                onChange={(e) => handleTableNumberChange(e.target.value)}
                placeholder="Enter table number..."
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              />
              {selectedBooking.tableNumber && (
                <button
                  onClick={() => handleTableNumberChange('')}
                  className="text-xs text-gray-500 hover:text-red-600 underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Guest Notes */}
          <div className="mb-4 bg-amber-50 rounded-lg p-4 border border-amber-200">
            <h2 className="text-sm font-semibold text-amber-800 uppercase mb-1 flex items-center gap-2">
              <MessageSquare size={14} /> Guest Notes
            </h2>
            <p className="text-xs text-amber-600 mb-2">Visible to guest — confirmation email resent automatically if date, time or notes change</p>
            <textarea
              value={selectedBooking.specialRequirements || ''}
              onChange={(e) => handleSpecialRequirementsChange(e.target.value)}
              placeholder="Dietary requirements, special occasion, accessibility needs..."
              rows={2}
              className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none bg-white"
            />
          </div>

          {/* Internal Notes */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h2 className="text-sm font-semibold text-gray-700 uppercase mb-1 flex items-center gap-2">
              <Lock size={14} /> Internal Notes
            </h2>
            <p className="text-xs text-gray-400 mb-2">Staff only — never sent to guest</p>
            <textarea
              value={selectedBooking.notes || ''}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Staff notes, table preference, special instructions for team..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Booking Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase mb-4">Contact Details</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="text-gray-900 font-medium">{selectedBooking.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="text-gray-900 font-medium">{selectedBooking.email || 'N/A'}</p>
                </div>
                {clientExists && (
                  <p className="text-xs text-emerald-600 font-medium">Linked to client database</p>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase mb-4">Booking Details</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Date & Time</p>
                  <p className="text-gray-900 font-medium">
                    {selectedBooking.date} at {selectedBooking.time}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Covers</p>
                  <p className="text-gray-900 font-medium">{selectedBooking.covers}</p>
                </div>
                <div>
                  <p className="text-gray-500">Venue</p>
                  <p className="text-gray-900 font-medium">
                    {TABLE_VENUES.find((v) => v.id === selectedBooking.venue)?.name || 'Unknown'}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase mb-4">Party Information</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Party Composition</p>
                  <p className="text-gray-900 font-medium">{selectedBooking.partyComposition || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Hotel Room</p>
                  <p className="text-gray-900 font-medium">{selectedBooking.roomNumber || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Package</p>
                  <p className="text-gray-900 font-medium">
                    {selectedBooking.packageName
                      ? `${selectedBooking.packageName}${packageLabel(selectedBooking, restaurantPackages).detail ? ` — ${packageLabel(selectedBooking, restaurantPackages).detail}` : ''}`
                      : 'À la carte'}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase mb-4">Additional Info</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Source</p>
                  <p className="text-gray-900 font-medium capitalize">{selectedBooking.source}</p>
                </div>
                {selectedBooking.seatedAt && (
                  <div>
                    <p className="text-gray-500">Seated At</p>
                    <p className="text-gray-900 font-medium">{new Date(selectedBooking.seatedAt).toLocaleTimeString('en-IE')}</p>
                  </div>
                )}
                {selectedBooking.completedAt && (
                  <div>
                    <p className="text-gray-500">Completed At</p>
                    <p className="text-gray-900 font-medium">{new Date(selectedBooking.completedAt).toLocaleTimeString('en-IE')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Guest Notes shown in the editable section above the info grid — removed from here */}

            {/* Payments Section */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase">Payments</h2>
                <button
                  onClick={() => setShowPaymentForm(!showPaymentForm)}
                  className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  <CreditCard size={14} />
                  {showPaymentForm ? 'Cancel' : 'Record Payment'}
                </button>
              </div>

              {/* Existing payments list */}
              {bookingPayments.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {bookingPayments.map((pmt) => (
                    <div key={pmt.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900">&euro;{parseFloat(pmt.amount).toFixed(2)}</span>
                        <span className="text-gray-500 ml-2">{pmt.category || 'payment'}</span>
                        <span className="text-gray-400 ml-2">({pmt.payment_type || pmt.paymentType || 'card'})</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-gray-500 text-xs">{pmt.payment_date || pmt.paymentDate}</span>
                        <button
                          type="button"
                          onClick={() => handleDeletePayment(pmt)}
                          title="Delete this payment"
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                          aria-label="Delete payment"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 text-right">
                    Total: &euro;{bookingPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-4">No payments recorded</p>
              )}

              {/* Record payment form */}
              {showPaymentForm && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Amount (&euro;)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={paymentData.amount}
                        onChange={e => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Payment Type</label>
                      <select
                        value={paymentData.payment_type}
                        onChange={e => setPaymentData(prev => ({ ...prev, payment_type: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="card">Card</option>
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cheque">Cheque</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                    <select
                      value={paymentData.category}
                      onChange={e => setPaymentData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="payment">Payment</option>
                      <option value="deposit">Deposit (manual)</option>
                      <option value="full_payment">Full Payment</option>
                      <option value="partial">Partial Payment</option>
                      <option value="refund">Refund</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                    <input
                      type="text"
                      value={paymentData.notes}
                      onChange={e => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Payment note..."
                    />
                  </div>
                  <button
                    onClick={handleRecordPayment}
                    disabled={!paymentData.amount || parseFloat(paymentData.amount) <= 0}
                    className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Record Payment
                  </button>
                </div>
              )}
            </div>
          </div>

          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 max-w-sm w-full">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Booking?</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete the booking for <strong>{selectedBooking.guestName}</strong>? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 h-10 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteBooking}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 h-10 rounded-lg font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
      {/* Booking Success Toast */}
      {bookingSuccess && (
        <div className="fixed top-4 right-4 z-[60] animate-slide-in">
          <div className="bg-emerald-600 text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            <span className="text-sm font-medium">{bookingSuccess}</span>
            <button onClick={() => setBookingSuccess(null)} className="ml-2 text-white/80 hover:text-white text-lg shrink-0">&times;</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Restaurant Table Management</h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1">Table bookings and availability</p>
        </div>
        <button
          onClick={() => setShowRestaurantSheet(true)}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 h-10 rounded-lg text-sm font-medium transition-colors w-fit"
          title="Preview and print restaurant function sheet for this date"
        >
          <Printer size={16} />
          Print Restaurant Sheet
        </button>
      </div>

      {/* Navigation Controls */}
      <div className="no-print bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Date Nav */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const d = parseLocalDate(selectedDate) || new Date();
                if (tableView === 'daily') d.setDate(d.getDate() - 1);
                else if (tableView === 'weekly') d.setDate(d.getDate() - 7);
                else d.setMonth(d.getMonth() - 1);
                setSelectedDate(toLocalDateKey(d));
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="text-center min-w-[200px]">
              <h2 className="text-lg font-bold text-gray-900">
                {tableView === 'daily' && new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                {tableView === 'weekly' && (() => {
                  const d = new Date(selectedDate + 'T12:00:00');
                  const day = d.getDay();
                  const mon = new Date(d);
                  mon.setDate(d.getDate() - ((day + 6) % 7));
                  const sun = new Date(mon);
                  sun.setDate(mon.getDate() + 6);
                  return `${mon.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })} – ${sun.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}`;
                })()}
                {tableView === 'monthly' && new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-IE', { month: 'long', year: 'numeric' })}
              </h2>
            </div>

            <button
              onClick={() => {
                const d = parseLocalDate(selectedDate) || new Date();
                if (tableView === 'daily') d.setDate(d.getDate() + 1);
                else if (tableView === 'weekly') d.setDate(d.getDate() + 7);
                else d.setMonth(d.getMonth() + 1);
                setSelectedDate(toLocalDateKey(d));
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronRight size={20} />
            </button>

            <button
              onClick={() => setSelectedDate(todayKey())}
              className="ml-2 px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
            >
              Today
            </button>

            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="ml-2 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* View Toggle + Add */}
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {['daily', 'weekly', 'monthly'].map((v) => (
                <button
                  key={v}
                  onClick={() => setTableView(v)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${tableView === v ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
            <button
              onClick={handleAddBooking}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 h-10 rounded-lg flex items-center gap-2 text-sm font-medium"
            >
              <Plus size={16} /> Add Booking
            </button>
          </div>
        </div>
      </div>

      {/* ═══ WEEKLY VIEW ═══ */}
      {tableView === 'weekly' && (() => {
        const d = parseLocalDate(selectedDate) || new Date();
        const day = d.getDay();
        const mon = new Date(d);
        mon.setDate(d.getDate() - ((day + 6) % 7));
        const weekDays = Array.from({ length: 7 }, (_, i) => {
          const wd = new Date(mon);
          wd.setDate(mon.getDate() + i);
          return toLocalDateKey(wd);
        });
        const allBookings = tableBookings || [];

        return (
          <div className="no-print bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs md:text-sm">
                <thead>
                  <tr>
                    <th className="bg-gray-100 border border-gray-300 p-2 md:p-3 text-left text-xs font-semibold text-gray-900 w-28">Venue</th>
                    {weekDays.map((dateStr) => {
                      const dt = new Date(dateStr + 'T12:00:00');
                      const isToday = dateStr === todayKey();
                      return (
                        <th key={dateStr} className={`border border-gray-300 p-2 md:p-3 text-center text-xs font-semibold ${isToday ? 'bg-emerald-50 text-emerald-900' : 'bg-gray-100 text-gray-900'}`}>
                          <div>{dt.toLocaleDateString('en-IE', { weekday: 'short' })}</div>
                          <div className="text-xs font-normal text-gray-600">{dt.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}</div>
                        </th>
                      );
                    })}
                    <th className="bg-gray-100 border border-gray-300 p-2 md:p-3 text-center text-xs font-semibold text-gray-900">Week Total</th>
                  </tr>
                </thead>
                <tbody>
                  {TABLE_VENUES.map((venue) => {
                    let weekTotal = 0;
                    let weekCovers = 0;
                    return (
                      <tr key={venue.id}>
                        <td className="bg-gray-50 border border-gray-300 p-2 md:p-3 font-semibold text-gray-900 text-xs">
                          {venue.name}
                          <div className="text-xs font-normal text-gray-500">Cap: {venue.capacity}</div>
                        </td>
                        {weekDays.map((dateStr) => {
                          const dayBookings = allBookings.filter(b => b.date === dateStr && b.venue === venue.id && b.status !== 'linked_to_event' && b.status !== 'cancelled' && b.status !== 'no_show');
                          const covers = dayBookings.reduce((sum, b) => sum + (b.covers || 0), 0);
                          const pct = venue.capacity > 0 ? (covers / venue.capacity) * 100 : 0;
                          weekTotal += dayBookings.length;
                          weekCovers += covers;
                          const isToday = dateStr === todayKey();
                          return (
                            <td
                              key={dateStr}
                              onClick={() => { setSelectedDate(dateStr); setTableView('daily'); }}
                              className={`border border-gray-300 p-2 md:p-3 text-center cursor-pointer hover:bg-blue-50 transition ${isToday ? 'bg-emerald-50/50' : ''}`}
                            >
                              {dayBookings.length > 0 ? (
                                <>
                                  <div className="text-lg font-bold text-gray-900">{dayBookings.length}</div>
                                  <div className={`text-xs font-medium ${pct >= 100 ? 'text-red-600' : pct >= 75 ? 'text-orange-600' : 'text-gray-500'}`}>
                                    {covers} covers
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                    <div className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 75 ? 'bg-orange-500' : pct >= 50 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                  </div>
                                </>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="border border-gray-300 p-2 md:p-3 text-center bg-gray-50">
                          <div className="text-lg font-bold text-gray-900">{weekTotal}</div>
                          <div className="text-xs text-gray-500">{weekCovers} covers</div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr>
                    <td className="bg-gray-100 border border-gray-300 p-2 md:p-3 font-bold text-gray-900 text-xs">All Venues</td>
                    {weekDays.map((dateStr) => {
                      const dayAll = allBookings.filter(b => b.date === dateStr && b.status !== 'linked_to_event' && b.status !== 'cancelled' && b.status !== 'no_show');
                      const coversAll = dayAll.reduce((sum, b) => sum + (b.covers || 0), 0);
                      return (
                        <td key={dateStr} className="bg-gray-100 border border-gray-300 p-2 md:p-3 text-center">
                          <div className="text-sm font-bold text-gray-900">{dayAll.length}</div>
                          <div className="text-xs text-gray-500">{coversAll} covers</div>
                        </td>
                      );
                    })}
                    <td className="bg-gray-200 border border-gray-300 p-2 md:p-3 text-center">
                      {(() => {
                        const totalAll = allBookings.filter(b => weekDays.includes(b.date) && b.status !== 'linked_to_event' && b.status !== 'cancelled' && b.status !== 'no_show');
                        return (
                          <>
                            <div className="text-lg font-bold text-gray-900">{totalAll.length}</div>
                            <div className="text-xs text-gray-500">{totalAll.reduce((s, b) => s + (b.covers || 0), 0)} covers</div>
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ═══ MONTHLY VIEW ═══ */}
      {tableView === 'monthly' && (() => {
        const d = new Date(selectedDate + 'T12:00:00');
        const year = d.getFullYear();
        const month = d.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startOffset = (firstDay.getDay() + 6) % 7; // Monday start
        const totalDays = lastDay.getDate();
        const allBookings = tableBookings || [];
        const todayStr = todayKey();

        const weeks = [];
        let currentWeek = new Array(startOffset).fill(null);
        for (let day = 1; day <= totalDays; day++) {
          currentWeek.push(day);
          if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
        }
        if (currentWeek.length > 0) {
          while (currentWeek.length < 7) currentWeek.push(null);
          weeks.push(currentWeek);
        }

        return (
          <div className="no-print bg-white rounded-lg shadow overflow-hidden p-4 md:p-6">
            <div className="grid grid-cols-7 gap-1 text-xs">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <div key={d} className="text-center font-semibold text-gray-500 py-2">{d}</div>
              ))}
              {weeks.flat().map((day, i) => {
                if (!day) return <div key={`empty-${i}`} className="bg-gray-50 rounded-lg min-h-[80px]" />;
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isToday = dateStr === todayStr;
                const dayBookings = allBookings.filter(b => b.date === dateStr && b.status !== 'linked_to_event' && b.status !== 'cancelled' && b.status !== 'no_show');
                const covers = dayBookings.reduce((sum, b) => sum + (b.covers || 0), 0);

                // Group by venue
                const venueGroups = {};
                dayBookings.forEach(b => {
                  const vn = TABLE_VENUES.find(v => v.id === b.venue)?.name || b.venue;
                  if (!venueGroups[vn]) venueGroups[vn] = { count: 0, covers: 0 };
                  venueGroups[vn].count++;
                  venueGroups[vn].covers += b.covers || 0;
                });

                return (
                  <div
                    key={dateStr}
                    onClick={() => { setSelectedDate(dateStr); setTableView('daily'); }}
                    className={`rounded-lg border min-h-[80px] p-1.5 cursor-pointer hover:ring-2 hover:ring-blue-300 transition ${isToday ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-emerald-700' : 'text-gray-700'}`}>{day}</div>
                    {dayBookings.length > 0 ? (
                      <>
                        <div className="text-xs font-bold text-gray-900">{dayBookings.length} bookings</div>
                        <div className="text-[10px] text-gray-500">{covers} covers</div>
                        {Object.entries(venueGroups).map(([vn, data]) => (
                          <div key={vn} className="text-[10px] text-gray-400 truncate">{vn}: {data.count}</div>
                        ))}
                      </>
                    ) : (
                      <div className="text-[10px] text-gray-300 mt-2">No bookings</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ═══ DAILY VIEW ═══ */}
      {tableView === 'daily' && <>
      {/* Main Grid */}
      <div className="no-print bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr>
                <th className="bg-gray-100 border border-gray-300 p-2 md:p-3 text-left text-xs md:text-sm font-semibold text-gray-900 w-24 md:w-32">
                  Venue
                </th>
                {SITTINGS.map((sitting) => (
                  <th
                    key={sitting.id}
                    className="bg-gray-100 border border-gray-300 p-2 md:p-3 text-center text-xs md:text-sm font-semibold text-gray-900 min-w-64 md:min-w-80"
                  >
                    {sitting.label}
                    <div className="text-xs font-normal text-gray-600">{sitting.time}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE_VENUES.map((venue) => (
                <tr key={venue.id}>
                  <td className="bg-gray-50 border border-gray-300 p-2 md:p-3 text-xs md:text-sm">
                    <p className="font-semibold text-gray-900">{venue.name}</p>
                    <p className="text-xs font-normal text-gray-500 mt-0.5">Cap: {venue.capacity}</p>
                    {/* Active block chips */}
                    {(() => {
                      const vBlocks = getBlocksForVenue(venue.id);
                      if (vBlocks.length === 0) return null;
                      return (
                        <div className="mt-1.5 flex flex-wrap gap-0.5">
                          {vBlocks.map((b) => (
                            <span
                              key={b.sittingId}
                              className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                b.blockType === 'closed'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}
                            >
                              {b.blockType === 'closed' ? <Ban size={8} /> : '✓'}
                              {b.sittingId === 'all_day'
                                ? 'All Day'
                                : b.sittingId.charAt(0).toUpperCase() + b.sittingId.slice(1)}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    <button
                      onClick={() => openBlockModal(venue)}
                      className="mt-2 flex items-center gap-1 text-[10px] text-gray-400 hover:text-indigo-600 font-medium transition"
                      title={`Manage sittings for ${venue.name}`}
                    >
                      <Ban size={10} /> Manage Sittings
                    </button>
                  </td>
                  {SITTINGS.map((sitting) => {
                    const vBlocks = getBlocksForVenue(venue.id);
                    const allDayBlock = vBlocks.find((b) => b.sittingId === 'all_day');
                    const sittingBlock = vBlocks.find((b) => b.sittingId === sitting.id);
                    const availableForDate = getSittingsForVenueAndDate(venue.id, selectedDate, vBlocks);
                    const isOpen = availableForDate.some((s) => s.id === sitting.id);

                    // Is this sitting open only because of an explicit 'open' override?
                    const isOverrideOpen =
                      sittingBlock?.blockType === 'open' ||
                      (!sittingBlock && allDayBlock?.blockType === 'open');

                    if (!isOpen) {
                      // Determine best label for why it's closed
                      const isStaffBlock = sittingBlock?.blockType === 'closed' || allDayBlock?.blockType === 'closed';
                      const blockRec = sittingBlock?.blockType === 'closed' ? sittingBlock : allDayBlock;
                      const reason = isStaffBlock
                        ? (blockRec?.reason || (sittingBlock?.blockType === 'closed' ? 'Blocked' : 'Closed for the day'))
                        : !sitting.venues.includes(venue.id)
                        ? 'Not available'
                        : (venue.id === 'brewery' || venue.id === 'randr') && !isWeekend(selectedDate)
                        ? 'Closed Mon–Thu'
                        : 'Not available';
                      return (
                        <td key={`${venue.id}-${sitting.id}`} className={`border border-gray-300 p-2 md:p-3 ${isStaffBlock ? 'bg-red-50' : 'bg-gray-100'}`}>
                          {isStaffBlock ? (
                            <div className="flex flex-col items-center justify-center py-4 gap-1 text-red-400">
                              <Ban size={18} />
                              <p className="text-xs font-semibold text-red-500">{reason}</p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic">{reason}</p>
                          )}
                        </td>
                      );
                    }

                    const covers = getTotalCoversForCell(venue.id, sitting.id);
                    const capacity = getVenueCapacityForSlot(venue.id, sitting.id);
                    const percentage = getCapacityPercentage(venue.id, sitting.id);
                    const load = getHalfHourLoad(venue.id, sitting.id);
                    const peakSlot = load.reduce((best, x) => (x.covers > (best ? best.covers : -1) ? x : best), null);

                    return (
                      <td key={`${venue.id}-${sitting.id}`} className="border border-gray-300 p-2 md:p-3">
                        <div className="space-y-2 md:space-y-3">
                          {/* Override-open badge */}
                          {isOverrideOpen && (
                            <div className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                              <span className="text-[10px] font-bold text-emerald-700">✓ Override Open</span>
                            </div>
                          )}
                          {/* Capacity Bar */}
                          <div className="space-y-1">
                            <div className="flex justify-between items-center gap-1">
                              <span className={`text-xs font-medium truncate ${percentage >= 100 ? 'text-red-600 font-bold' : 'text-gray-700'}`}>
                                {covers > 0 && peakSlot ? `Peak ${covers} / ${capacity} at ${peakSlot.label}` : `${covers} / ${capacity} covers`}
                              </span>
                              <span className={`text-xs shrink-0 ${percentage >= 100 ? 'text-red-600 font-bold' : 'text-gray-500'}`}>{Math.round(percentage)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div
                                className={`h-full ${getCapacityColor(percentage)} transition-all`}
                                style={{ width: `${Math.min(percentage, 100)}%` }}
                              />
                            </div>
                            {/* Half-hour load strip: covers in use at each half hour */}
                            {covers > 0 && load.length > 0 && (
                              <div className="flex gap-px mt-1" title="Covers in use at each half hour (2-hour table turn)">
                                {load.map((x) => {
                                  const pct = capacity > 0 ? (x.covers / capacity) * 100 : 0;
                                  const free = Math.max(0, capacity - x.covers);
                                  return (
                                    <div key={x.mins} className="flex-1 min-w-0 text-center" title={`${x.label}: ${x.covers} in use, ${free} free`}>
                                      <div className={`h-4 rounded-sm text-[9px] leading-4 font-semibold ${x.covers === 0 ? 'bg-gray-100 text-gray-400' : `${getCapacityColor(pct)} text-white`}`}>
                                        {free}
                                      </div>
                                      <div className="text-[8px] text-gray-400 leading-3">{x.mins % 60 === 0 ? x.label.slice(0, 2) : ''}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {covers > 0 && <p className="text-[10px] text-gray-400 -mt-0.5">Numbers = covers free at each half hour</p>}
                            {percentage >= 100 && (
                              <p className="text-xs text-red-600 font-semibold">Over capacity at the peak!</p>
                            )}
                            {/* Warn if there's a function event booked in this restaurant venue
                                during THIS sitting specifically. Previously this only checked
                                venue+date, so a single event (e.g. 21:00-23:30) showed the same
                                "Event booked here" banner under every sitting for that venue that
                                day — including Lunch, which it doesn't overlap at all. That trains
                                staff to stop trusting the warning, including the one sitting where
                                it's actually true. */}
                            {(() => {
                              const venueEvents = (events || []).filter((e) => {
                                if (e.venue_id !== venue.id || e.date !== selectedDate) return false;
                                const evStart = timeToMins(e.startTime);
                                const evEnd = timeToMins(e.endTime);
                                // No recorded time — fail safe and still show the warning rather
                                // than silently hiding a real conflict.
                                if (evStart == null || evEnd == null) return true;
                                const sitStart = sitting.startH * 60;
                                const sitEnd = sitting.endH * 60;
                                return evStart < sitEnd && evEnd > sitStart;
                              });
                              if (venueEvents.length === 0) return null;
                              return (
                                <div className="bg-amber-50 border border-amber-300 rounded p-1.5 mt-1">
                                  <p className="text-xs text-amber-700 font-semibold">Event booked here:</p>
                                  {venueEvents.map((e) => (
                                    <p key={e.id} className="text-xs text-amber-600">{(e.name || '').replace(/\s*\(from table\)\s*$/i, '')} ({e.guests} guests, {String(e.startTime || '').slice(0, 5)}–{String(e.endTime || '').slice(0, 5)})</p>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Bookings */}
                          <div className="space-y-1 md:space-y-2 max-h-80 md:max-h-96 overflow-y-auto">
                            {getAllBookingsForCell(venue.id, sitting.id).map((booking) => {
                              const isLinked = booking.status === 'linked_to_event';
                              const sDef = getStatusDef(booking.status);
                              return (
                                <button
                                  key={booking.id}
                                  onClick={() => setSelectedBooking(booking)}
                                  className={`w-full text-left p-1.5 md:p-2 rounded border-2 text-xs transition-all hover:shadow-md cursor-pointer ${isLinked ? 'bg-purple-50 border-purple-200 text-purple-400 opacity-60' : sDef.color}`}
                                >
                                  <div className="flex items-center justify-between gap-1">
                                    <p className={`font-semibold truncate ${isLinked ? 'line-through' : ''}`}>{booking.guestName}</p>
                                    {isLinked && <Link2 size={10} className="shrink-0 text-purple-400" />}
                                    {!isLinked && booking.tableNumber && (
                                      <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold bg-white/70 border border-current">
                                        <Hash size={10} />{booking.tableNumber}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs opacity-75">{booking.time} ({booking.covers} covers)</p>
                                  {!isLinked && (
                                    booking.tableNumber ? (
                                      <p className="text-xs font-semibold mt-0.5">Table {booking.tableNumber}</p>
                                    ) : (
                                      <p className="text-xs italic opacity-60 mt-0.5">No table assigned</p>
                                    )
                                  )}
                                  <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs ${isLinked ? 'bg-purple-100 text-purple-500' : sDef.badge}`}>
                                    {isLinked ? 'Moved to Event' : sDef.short}
                                  </span>
                                </button>
                              );
                            })}
                            <button
                              onClick={() => handleAddBookingForSlot(venue.id, sitting.id)}
                              className="w-full text-center py-1.5 md:py-2 rounded border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition text-xs text-gray-400 hover:text-blue-600 font-medium cursor-pointer"
                            >
                              + Add Booking
                            </button>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="no-print bg-white rounded-lg shadow p-4 md:p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Status Legend</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3 text-xs">
          {BOOKING_STATUSES.map((s) => (
            <div key={s.id} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded ${s.dot}`}></span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
      </>}

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 max-w-2xl w-full my-4 max-h-none">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                {isEditMode ? 'Edit Booking' : 'Add New Booking'}
              </h2>
              <button
                onClick={handleCancelEdit}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
                <input
                  type="text"
                  name="guestName"
                  value={formData.guestName}
                  onChange={handleFormChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Venue *</label>
                  <select
                    name="venue"
                    value={formData.venue}
                    onChange={handleFormChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select venue</option>
                    {TABLE_VENUES.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sitting *</label>
                  <select
                    name="sitting"
                    value={formData.sitting}
                    onChange={handleFormChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select sitting</option>
                    {getValidSittingsForVenue(formData.venue).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label} ({s.time})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date || selectedDate}
                    onChange={handleFormChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Covers *</label>
                  <input
                    type="number"
                    name="covers"
                    value={formData.covers}
                    onChange={handleFormChange}
                    min="1"
                    max={getVenueCapacityForDate(formData.venue, formData.date || selectedDate, formData.sitting) || 60}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {formData.sitting && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time *</label>
                  {(() => {
                    const range = getTimeRangeForSitting(formData.sitting);
                    const startH = parseInt(range.start.split(':')[0]);
                    const endH = parseInt(range.end.split(':')[0]);
                    const hours = [];
                    for (let h = startH; h <= endH; h++) hours.push(h);
                    return (
                      <>
                        <div className="flex gap-1.5 overflow-x-auto pb-1">
                          {hours.map((h) => (
                            <button key={h} type="button" onClick={() => { setSelectedHour(h); setFormData((prev) => ({ ...prev, time: `${String(h).padStart(2,'0')}:00` })); }}
                              className={`shrink-0 px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${selectedHour === h ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                              {h > 12 ? `${h-12}pm` : h === 12 ? '12pm' : `${h}am`}
                            </button>
                          ))}
                        </div>
                        {selectedHour !== null && (
                          <div className="flex gap-1.5 mt-2">
                            {QUARTER_MINS.map((m) => {
                              const slot = `${String(selectedHour).padStart(2,'0')}:${m}`;
                              return (
                                <button key={m} type="button" onClick={() => setFormData((prev) => ({ ...prev, time: slot }))}
                                  className={`flex-1 py-1.5 rounded text-xs font-medium border transition-all ${formData.time === slot ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                                  {slot}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {formData.venue === 'reynards' && parseInt(formData.covers || 0) < 6 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-900 font-medium">
                    Reynards requires a minimum of 6 covers
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select
                    name="source"
                    value={formData.source}
                    onChange={handleFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="web">Web</option>
                    <option value="phone">Phone</option>
                    <option value="walk-in">Walk-in</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Number</label>
                  <input
                    type="text"
                    name="roomNumber"
                    value={formData.roomNumber}
                    onChange={handleFormChange}
                    placeholder="If hotel guest"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Package — printed with its inclusions on the daily function sheet */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Package</label>
                  <button type="button" onClick={() => setShowPackageManager(true)} className="text-xs text-blue-600 hover:underline">Manage packages</button>
                </div>
                <select
                  name="packageName"
                  value={formData.packageName || ''}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">À la carte (no package)</option>
                  {(restaurantPackages || [])
                    .filter((p) => p.active || p.name === formData.packageName)
                    .map((p) => (
                      <option key={p.id} value={p.name}>
                        {p.name}{p.inclusions ? ` — ${p.inclusions}` : ''}{p.pricePp ? ` (€${p.pricePp} pp)` : ''}
                      </option>
                    ))}
                </select>
                {(() => {
                  const pkg = findPackage(restaurantPackages, formData.packageName);
                  if (!pkg || !pkg.kitchenOrderDayBefore || !formData.date) return null;
                  const tomorrow = toLocalDateKey(addDays(todayKey(), 1));
                  const bookingKey = typeof formData.date === 'string' ? formData.date.slice(0, 10) : toLocalDateKey(formData.date);
                  if (bookingKey > tomorrow) return null;
                  return (
                    <p className="mt-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                      ⚠ {pkg.name} must be ordered by the kitchen the day before. This booking is for {bookingKey === todayKey() ? 'today' : 'tomorrow'}, so tell the kitchen directly.
                    </p>
                  );
                })()}
              </div>

              {/* Guest Notes — visible to guest, goes in confirmation email */}
              <div>
                <label className="block text-sm font-medium text-amber-800 mb-1">Guest Notes</label>
                <p className="text-xs text-amber-600 mb-2">Sent in guest confirmation email — dietary, occasion, accessibility</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {QUICK_TAGS.map((tag) => {
                    const active = (formData.specialRequirements || '').includes(tag);
                    return (
                      <button key={tag} type="button"
                        onClick={() => setFormData((prev) => {
                          const current = prev.specialRequirements || '';
                          if (current.includes(tag)) return { ...prev, specialRequirements: current.replace(tag, '').replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '').trim() };
                          return { ...prev, specialRequirements: current ? `${current}, ${tag}` : tag };
                        })}
                        className={`px-2 py-1 rounded-full text-xs font-medium border transition-all ${active ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400 hover:bg-amber-50'}`}>
                        {tag}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  name="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={handleFormChange}
                  placeholder="Or type guest requirements..."
                  className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-amber-50"
                />
              </div>

              {/* Internal Notes — staff only, never emailed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Lock size={12} /> Internal Notes
                </label>
                <p className="text-xs text-gray-400 mb-2">Staff only — never sent to guest</p>
                <input
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  placeholder="Table preference, team instructions, follow-up needed..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="flex-1 px-4 py-2 h-10 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 h-10 text-sm font-medium text-white rounded-lg transition"
                  style={{ backgroundColor: '#1A5C3A' }}
                >
                  {isEditMode ? 'Update Booking' : 'Add Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Restaurant Function Sheet Modal */}
      {showRestaurantSheet && (
        <div className="fixed inset-0 bg-black/60 z-50 flex flex-col">
          <div className="no-print bg-gray-900 text-white px-4 md:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
            <h2 className="text-lg font-semibold">Restaurant Function Sheet Preview</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-5 py-2 h-10 rounded-lg font-medium text-sm text-white w-full sm:w-auto"
                style={{ backgroundColor: '#1A5C3A' }}
              >
                <Printer size={16} />
                Print
              </button>
              <button
                onClick={() => setShowRestaurantSheet(false)}
                className="p-2 h-10 w-10 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-200 p-4 md:p-6 print:p-0 print:bg-white">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-4 md:p-8 print:shadow-none print:rounded-none print:max-w-none">
              <div className="border-b-2 border-gray-300 pb-4 mb-6">
                <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#1A5C3A' }}>
                  Errigal Country House Hotel
                </h1>
                <h2 className="text-lg md:text-xl text-gray-600 mt-1">Restaurant Function Sheet</h2>
              </div>
              <p className="text-base md:text-lg font-semibold text-gray-900 mb-6">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>

              {TABLE_VENUES.map((venue) => {
                const venueBookings = bookingsForDate
                  .filter((b) => b.venue === venue.id && b.status !== 'cancelled')
                  .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
                const totalCovers = venueBookings.reduce((sum, b) => sum + (b.covers || 0), 0);

                return (
                  <div key={venue.id} className="mb-6 md:mb-8">
                    <div className="bg-gray-100 px-3 md:px-4 py-2 border-l-4 border-gray-800 mb-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <h3 className="text-base md:text-lg font-bold text-gray-900">{venue.name}</h3>
                      <span className="text-xs md:text-sm text-gray-600">
                        {venueBookings.length} bookings — {totalCovers} covers
                      </span>
                    </div>
                    {venueBookings.length === 0 ? (
                      <p className="text-xs md:text-sm text-gray-400 italic ml-4">No bookings</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs md:text-sm border-collapse">
                          <thead>
                            <tr className="border-b-2 border-gray-300">
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Time</th>
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Guest</th>
                              <th className="text-center py-2 px-1 md:px-2 font-semibold text-gray-700">Covers</th>
                              <th className="text-center py-2 px-1 md:px-2 font-semibold text-gray-700">Table</th>
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Party</th>
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Room</th>
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Status</th>
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Special Req.</th>
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Notes</th>
                              <th className="text-left py-2 px-1 md:px-2 font-semibold text-gray-700">Payments</th>
                            </tr>
                          </thead>
                          <tbody>
                            {venueBookings.map((b) => {
                              const bPayments = (payments || []).filter(
                                (p) => p.booking_id === b.id || p.bookingId === b.id
                              );
                              const paidTotal = bPayments.reduce(
                                (sum, p) => sum + parseFloat(p.amount || 0),
                                0
                              );
                              return (
                                <tr key={b.id} className="border-b border-gray-200 align-top">
                                  <td className="py-2 px-1 md:px-2">{b.time}</td>
                                  <td className="py-2 px-1 md:px-2">{b.guestName}</td>
                                  <td className="py-2 px-1 md:px-2 text-center font-medium">{b.covers}</td>
                                  <td className="py-2 px-1 md:px-2 text-center">{b.tableNumber || '—'}</td>
                                  <td className="py-2 px-1 md:px-2 text-xs">{b.partyComposition || '—'}</td>
                                  <td className="py-2 px-1 md:px-2">{b.roomNumber || '—'}</td>
                                  <td className="py-2 px-1 md:px-2">
                                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(b.status)}`}>
                                      {getStatusDef(b.status).short}
                                    </span>
                                  </td>
                                  <td className="py-2 px-1 md:px-2 text-xs max-w-xs">
                                    {b.specialRequirements || '—'}
                                  </td>
                                  <td className="py-2 px-1 md:px-2 text-xs max-w-xs whitespace-pre-wrap">
                                    {b.notes || '—'}
                                  </td>
                                  <td className="py-2 px-1 md:px-2 text-xs">
                                    {bPayments.length === 0 ? (
                                      '—'
                                    ) : (
                                      <div className="space-y-1">
                                        {bPayments.map((pmt) => (
                                          <div key={pmt.id} className="leading-tight">
                                            <span className="font-medium">&euro;{parseFloat(pmt.amount || 0).toFixed(2)}</span>
                                            <span className="text-gray-500 ml-1">
                                              {(pmt.category || 'payment').replace('_', ' ')}
                                            </span>
                                            <span className="text-gray-400 ml-1">
                                              ({pmt.payment_type || pmt.paymentType || 'card'})
                                            </span>
                                          </div>
                                        ))}
                                        <div className="text-gray-600 font-medium pt-0.5 border-t border-gray-100">
                                          Total: &euro;{paidTotal.toFixed(2)}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="mt-8 pt-6 border-t border-gray-300 text-xs text-gray-500">
                <p>Generated on {new Date().toLocaleDateString('en-IE')} at {new Date().toLocaleTimeString('en-IE')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPackageManager && <PackageManager onClose={() => setShowPackageManager(false)} />}

      {/* ─── Manage Sittings Modal ─────────────────────────────────────────────── */}
      {blockModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <div>
                <p className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                  <Ban size={14} className="text-indigo-500" />
                  Manage Sittings — {blockModal.venueName}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{selectedDate}</p>
              </div>
              <button onClick={() => setBlockModal(null)} className="text-gray-400 hover:text-gray-600 p-1">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Per-sitting toggles */}
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-1 mb-1">
                  <span className="text-[10px] text-gray-400 font-medium col-span-1">Sitting</span>
                  <span className="text-[10px] text-gray-400 font-medium text-center">Default</span>
                  <span className="text-[10px] text-gray-400 font-medium text-center">Closed</span>
                  <span className="text-[10px] text-gray-400 font-medium text-center">Open</span>
                </div>
                {[
                  { id: 'all_day', label: 'All Day' },
                  ...SITTINGS.filter((s) => s.venues.includes(blockModal.venueId)),
                ].map((item) => {
                  const current = blockChanges[item.id] || 'default';
                  return (
                    <div key={item.id} className="grid grid-cols-4 gap-1 items-center">
                      <span className="text-xs font-medium text-gray-700 col-span-1 truncate">{item.label || item.id}</span>
                      {['default', 'closed', 'open'].map((option) => (
                        <button
                          key={option}
                          onClick={() => setBlockChanges((prev) => ({ ...prev, [item.id]: option }))}
                          className={`py-1.5 text-[11px] font-semibold rounded border transition ${
                            current === option
                              ? option === 'closed'
                                ? 'bg-red-600 border-red-600 text-white'
                                : option === 'open'
                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                : 'bg-gray-500 border-gray-500 text-white'
                              : 'bg-white border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600'
                          }`}
                        >
                          {option === 'default' ? '—' : option === 'closed' ? 'Close' : 'Open'}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 leading-relaxed">
                <strong className="text-red-600">Closed</strong> blocks new bookings for that sitting.{' '}
                <strong className="text-emerald-600">Open</strong> forces a sitting open even on normally-closed days (e.g. Brewery lunch on Monday).{' '}
                <strong>—</strong> restores the default schedule rule.
              </p>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Reason (optional — shown in grid)</label>
                <input
                  type="text"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="e.g. Private event, Maintenance, Staff training..."
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>

              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Existing bookings are not removed — changes only affect whether new bookings can be added.
              </p>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setBlockModal(null)}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBlocks}
                  disabled={blockSaving}
                  className="flex-1 py-2 text-sm bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition"
                >
                  {blockSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
